import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Briefcase, MapPin, Clock, Navigation, CheckCircle2,
  AlertTriangle, Send, Locate, Timer, Home, Loader2, Copy, Link2, ClipboardCheck
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import JobPhotos from "@/components/JobPhotos";
import JobReceipts from "@/components/JobReceipts";
import DamageAssessment from "@/components/DamageAssessment";
import TechExpenses from "@/components/TechExpenses";
import JobMap from "@/components/JobMap";
import ChatPanel from "@/components/ChatPanel";
import JobInventory from "@/components/JobInventory";
import JobChecklist from "@/components/JobChecklist";
import MoistureMapping from "@/components/MoistureMapping";
import ContentGuard from "@/components/ContentGuard";
import SectionProgress from "@/components/SectionProgress";
import { hasPermission } from "@/lib/permissions";
import { useAuth } from "@/lib/auth";

function ElapsedTimer({ since, warn = false }) {
  const [elapsed, setElapsed] = useState("");
  const [totalMinutes, setTotalMinutes] = useState(0);

  useEffect(() => {
    if (!since) return;
    const update = () => {
      const diff = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(
        `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
      );
      setTotalMinutes(Math.floor(diff / 60));
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [since]);

  const isLate = totalMinutes >= 45;

  return (
    <span
      className={`timer-display text-sm font-bold ${
        isLate ? "text-red-600" : warn && totalMinutes >= 30 ? "text-amber-600" : "text-zinc-900"
      }`}
      data-testid="elapsed-timer"
    >
      {elapsed}
      {isLate && (
        <span className="ml-1.5 text-[10px] font-medium text-red-500 urgent-flash">LATE</span>
      )}
    </span>
  );
}

export default function ActiveJobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [gpsActive, setGpsActive] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [lateDialogJob, setLateDialogJob] = useState(null);
  const [lateReason, setLateReason] = useState("");
  const [submittingReason, setSubmittingReason] = useState(false);
  const [acceptancePinDialog, setAcceptancePinDialog] = useState(null);
  const watchIdRef = useRef(null);
  const locationIntervalRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await API.get("/requests/active");
      setJobs(data.requests);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const i = setInterval(fetchJobs, 10000);
    return () => clearInterval(i);
  }, [fetchJobs]);

  // GPS tracking for en_route jobs
  const hasEnRouteJob = jobs.some((j) => j.current_status === "en_route");

  useEffect(() => {
    if (hasEnRouteJob && !gpsActive && navigator.geolocation) {
      setGpsActive(true);
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => {
          console.error("GPS error:", err);
          toast.error("Unable to access location. Please enable GPS.");
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      );

      // Send location to backend every 15 seconds
      locationIntervalRef.current = setInterval(async () => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              try {
                await API.put("/technicians/me/location", {
                  latitude: pos.coords.latitude,
                  longitude: pos.coords.longitude,
                });
              } catch (e) {
                console.error("Location update failed:", e);
              }
            },
            () => {},
            { enableHighAccuracy: true, timeout: 10000 }
          );
        }
      }, 15000);
    } else if (!hasEnRouteJob && gpsActive) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
      setGpsActive(false);
      setGpsCoords(null);
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, [hasEnRouteJob, gpsActive]);

  const markEnRoute = async (jobId) => {
    setActionLoading(jobId);
    try {
      await API.post(`/requests/${jobId}/en-route`);
      toast.success("Marked en route - GPS tracking active");
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setActionLoading(null);
    }
  };

  const [showArrivalConfirm, setShowArrivalConfirm] = useState(null);
  const [arrivalData, setArrivalData] = useState(null);
  const [homeownerCode, setHomeownerCode] = useState("");
  const [confirmingHomeowner, setConfirmingHomeowner] = useState(false);

  const markArrived = async (jobId) => {
    // Show confirmation dialog first
    setShowArrivalConfirm(jobId);
  };

  const confirmArrival = async () => {
    if (!showArrivalConfirm) return;
    setActionLoading(showArrivalConfirm);
    try {
      const { data } = await API.post(`/requests/${showArrivalConfirm}/arrive`);
      setArrivalData(data);
      toast.success("Your arrival is confirmed! Share the code with the homeowner.");
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setShowArrivalConfirm(null);
    } finally {
      setActionLoading(null);
    }
  };

  const confirmHomeowner = async (jobId, code) => {
    setConfirmingHomeowner(true);
    try {
      await API.post(`/requests/${jobId}/homeowner-confirm`, { code });
      toast.success("Homeowner arrival confirmed!");
      setShowArrivalConfirm(null);
      setArrivalData(null);
      setHomeownerCode("");
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setConfirmingHomeowner(false);
    }
  };

  const completeJob = async (jobId) => {
    setActionLoading(jobId);
    try {
      // Check if all items are approved before completing
      try {
        const { data: check } = await API.get(`/photos/completion-check/${jobId}`);
        if (!check.can_complete) {
          toast.error(check.blockers.join(". "), { duration: 6000 });
          setActionLoading(null);
          return;
        }
      } catch (e) {
        // If completion-check fails, log but still try to complete (backend will block if needed)
        console.warn("Completion-check failed, continuing", e);
      }
      await API.put(`/requests/${jobId}/status`, { status: "completed" });
      toast.success("Job completed!");
      // Fetch the updated request to get the acceptance PIN
      try {
        const { data: updatedReq } = await API.get(`/requests/${jobId}`);
        if (updatedReq.acceptance_pin) {
          setAcceptancePinDialog({ jobId, pin: updatedReq.acceptance_pin, requestCode: updatedReq.request_code });
        }
      } catch (e) { console.error(e); }
      fetchJobs();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to complete job";
      toast.error(msg, { duration: 6000 });
      console.error("Complete job failed:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const submitLateReason = async () => {
    if (!lateDialogJob) {
      toast.error("No job selected");
      return;
    }
    if (!lateReason.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    setSubmittingReason(true);
    try {
      await API.post(`/requests/${lateDialogJob.id}/late-reason`, { reason: lateReason.trim() });
      toast.success("Late reason submitted to management");
      setLateDialogJob(null);
      setLateReason("");
      fetchJobs();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to submit late reason";
      toast.error(msg, { duration: 6000 });
      console.error("Late reason submission failed:", err);
    } finally {
      setSubmittingReason(false);
    }
  };

  const isJobLate = (job) => {
    if (!job.accepted_at) return false;
    const elapsed = (Date.now() - new Date(job.accepted_at).getTime()) / 60000;
    return elapsed >= 45 && job.current_status !== "in_progress";
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const statusLabels = {
    accepted: "Accepted",
    en_route: "On Route",
    in_progress: "On Site",
  };

  const statusColors = {
    accepted: "bg-blue-100 text-blue-700 border-blue-200",
    en_route: "bg-amber-100 text-amber-700 border-amber-200",
    in_progress: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  return (
    <div className="space-y-4" data-testid="active-jobs-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-zinc-900">Active Jobs</h2>
        <div className="flex items-center gap-2">
          {gpsActive && (
            <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200" data-testid="gps-active-indicator">
              <div className="w-2 h-2 rounded-full bg-red-500 gps-ping" />
              <Locate className="w-3 h-3" />
              <span className="font-medium">GPS Active</span>
            </div>
          )}
          <Badge variant="secondary">{jobs.length} active</Badge>
        </div>
      </div>

      {jobs.length === 0 ? (
        <Card className="border border-zinc-200">
          <CardContent className="p-8 text-center">
            <Briefcase className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No active jobs</p>
            <p className="text-xs text-zinc-400 mt-1">Accept incoming requests to start working</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => {
            const late = isJobLate(job);
            return (
              <Card
                key={job.id}
                className={`border overflow-hidden ${late ? "border-red-300 late-warning" : "border-zinc-200"}`}
                data-testid={`active-job-${job.id}`}
              >
                <CardContent className="p-4">
                  {/* Header with timer */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="font-mono text-xs text-zinc-400">{job.request_code}</span>
                      <h3 className="text-sm font-semibold text-zinc-900 mt-0.5">
                        {job.service_type_label}
                      </h3>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge
                        className={`text-[10px] border ${statusColors[job.current_status] || ""} hover:bg-inherit`}
                      >
                        {statusLabels[job.current_status] || job.current_status}
                      </Badge>
                    </div>
                  </div>

                  {/* Live Timer */}
                  <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl mb-3" data-testid={`job-timer-${job.id}`}>
                    <Timer className={`w-5 h-5 ${late ? "text-red-500" : "text-zinc-400"}`} />
                    <div className="flex-1">
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">
                        Response Time
                      </p>
                      <ElapsedTimer since={job.accepted_at} warn />
                    </div>
                    {late && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[10px] border-red-200 text-red-600 hover:bg-red-50 h-7 px-2"
                        onClick={() => { setLateDialogJob(job); setLateReason(""); }}
                        data-testid={`provide-reason-${job.id}`}
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" /> Provide Reason
                      </Button>
                    )}
                  </div>

                  {/* Late Warning Banner */}
                  {late && (
                    <div className="flex items-center gap-2 p-2.5 bg-red-50 rounded-lg mb-3 border border-red-200" data-testid={`late-warning-${job.id}`}>
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <p className="text-xs text-red-700 font-medium">
                        You are past the 45-minute response window. Management has been notified.
                      </p>
                    </div>
                  )}

                  {/* Restoration Inspection Wizard launcher */}
                  {(job.current_status === "in_progress" || job.current_status === "en_route") && (
                    <Button
                      onClick={() => navigate(`/vendor/inspection/${job.id}`)}
                      className="w-full min-h-12 h-auto py-2 mb-3 bg-amber-500 hover:bg-amber-600 text-white font-bold whitespace-normal leading-tight"
                      data-testid={`open-inspection-wizard-${job.id}`}>
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      {job.inspection_workflow?.current_step
                        ? `Continue Restoration Inspection (Step ${job.inspection_workflow.current_step}/15)`
                        : "Start Restoration Inspection Workflow"}
                    </Button>
                  )}

                  {/* Section Progress Indicator */}
                  {(job.current_status === "in_progress" || job.current_status === "en_route") && (
                    <SectionProgress job={job} user={user} />
                  )}

                  {/* GPS Info for en_route */}
                  {job.current_status === "en_route" && gpsCoords && (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg mb-3 border border-blue-100" data-testid={`gps-tracking-${job.id}`}>
                      <Navigation className="w-4 h-4 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-[10px] text-blue-600 font-bold uppercase">Tracking Location</p>
                        <p className="text-[10px] text-blue-500 font-mono">
                          {gpsCoords.lat.toFixed(5)}, {gpsCoords.lng.toFixed(5)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Arrival Confirmation Status */}
                  {job.current_status === "in_progress" && job.tech_arrival_confirmed && (
                    <div
                      className="flex items-center gap-2 p-2.5 rounded-lg mb-3 border cursor-pointer hover:opacity-90 transition-opacity"
                      data-testid={`arrival-status-${job.id}`}
                      style={{ background: job.homeowner_arrival_confirmed ? "#f0fdf4" : "#fffbeb", borderColor: job.homeowner_arrival_confirmed ? "#bbf7d0" : "#fde68a" }}
                      onClick={() => {
                        if (!job.homeowner_arrival_confirmed) {
                          setShowArrivalConfirm(job.id);
                          setArrivalData({ confirmation_code: job.arrival_confirmation_code });
                        }
                      }}
                    >
                      <CheckCircle2 className={`w-4 h-4 ${job.homeowner_arrival_confirmed ? "text-emerald-600" : "text-amber-600"}`} />
                      <div className="flex-1">
                        <p className="text-xs font-medium" style={{ color: job.homeowner_arrival_confirmed ? "#166534" : "#92400e" }}>
                          {job.homeowner_arrival_confirmed
                            ? "Both confirmations received"
                            : "Awaiting homeowner confirmation"}
                        </p>
                        <p className="text-[10px]" style={{ color: job.homeowner_arrival_confirmed ? "#15803d" : "#a16207" }}>
                          {job.homeowner_arrival_confirmed
                            ? `Homeowner confirmed at ${new Date(job.homeowner_confirmed_at).toLocaleTimeString()}`
                            : `Code: ${job.arrival_confirmation_code || "—"} — Tap to share link`}
                        </p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-zinc-600 mb-3">{job.problem_description}</p>

                  <div className="space-y-1 mb-4">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <MapPin className="w-3 h-3" />
                      <span>{job.property_address_line1}, {job.property_city}</span>
                    </div>
                    {job.customer_name && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span className="w-3 font-bold text-center">C</span>
                        <span>
                          {job.customer_name}
                          {job.customer_phone && ` - ${job.customer_phone}`}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Clock className="w-3 h-3" />
                      <span>Accepted {job.accepted_at ? new Date(job.accepted_at).toLocaleString() : ""}</span>
                    </div>
                    {job.en_route_at && (
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Navigation className="w-3 h-3" />
                        <span>On Route {new Date(job.en_route_at).toLocaleTimeString()}</span>
                      </div>
                    )}
                    {job.arrived_at && (
                      <div className="flex items-center gap-2 text-xs text-emerald-600">
                        <Home className="w-3 h-3" />
                        <span>Arrived {new Date(job.arrived_at).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Map to Customer Location + Driving Data */}
                  {(job.current_status === "en_route" || job.current_status === "in_progress" || job.current_status === "accepted") && (
                    <div className="pt-2 border-t border-zinc-100">
                      <JobMap job={job} />
                    </div>
                  )}

                  {/* Chat with Homeowner */}
                  {(job.current_status === "en_route" || job.current_status === "in_progress" || job.current_status === "accepted") && (
                    <div className="pt-2 border-t border-zinc-100">
                      <ChatPanel requestId={job.id} customerName={job.customer_name} />
                    </div>
                  )}

                  {/* Damage Assessment - shown once arrived (in_progress) */}
                  {job.current_status === "in_progress" && (
                    <div id={`section-damage-${job.id}`} className="pt-2 border-t border-amber-200 bg-amber-50/20 -mx-4 px-4 pb-2 rounded-b-lg">
                      <DamageAssessment requestId={job.id} />
                    </div>
                  )}

                  {/* Job Photos */}
                  {(job.current_status === "in_progress" || job.current_status === "en_route") && (
                    <div id={`section-photos-${job.id}`} className="pt-2 border-t border-zinc-100">
                      <JobPhotos requestId={job.id} />
                    </div>
                  )}

                  {/* Receipts */}
                  {(job.current_status === "in_progress" || job.current_status === "en_route") && (
                    <div id={`section-receipts-${job.id}`} className="pt-2 border-t border-zinc-100">
                      <JobReceipts requestId={job.id} />
                    </div>
                  )}

                  {/* Expenses */}
                  {(job.current_status === "in_progress" || job.current_status === "en_route") && (
                    <div id={`section-expenses-${job.id}`} className="pt-2 border-t border-zinc-100">
                      <TechExpenses requestId={job.id} />
                    </div>
                  )}

                  {/* Inventory */}
                  {(job.current_status === "in_progress") && (
                    <div id={`section-inventory-${job.id}`} className="pt-2 border-t border-zinc-100">
                      <JobInventory requestId={job.id} />
                    </div>
                  )}

                  {/* Checklist */}
                  {(job.current_status === "in_progress") && (
                    <div id={`section-checklist-${job.id}`} className="pt-2 border-t border-zinc-100">
                      <JobChecklist requestId={job.id} />
                    </div>
                  )}

                  {/* Moisture Mapping - for water damage jobs */}
                  {job.current_status === "in_progress" && hasPermission(user, "tech.moisture") && (
                    <div id={`section-moisture-${job.id}`} className="pt-2 border-t border-zinc-100">
                      <MoistureMapping requestId={job.id} jobInfo={{
                        room: job.property_city,
                        cause: job.service_type_label,
                      }} />
                    </div>
                  )}

                  {/* ContentGuard™ — Contents inventory & chain-of-custody */}
                  {job.current_status === "in_progress" && hasPermission(user, "tech.contentguard") && (
                    <div id={`section-contentguard-${job.id}`}>
                      <ContentGuard requestId={job.id}
                                    requestLabel={`${job.request_code} · ${job.service_type_label}`} />
                    </div>
                  )}

                  {/* Action Buttons - Status Flow */}
                  <div className="flex gap-2">
                    {job.current_status === "accepted" && (
                      <Button
                        onClick={() => markEnRoute(job.id)}
                        disabled={actionLoading === job.id}
                        className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white text-sm"
                        data-testid={`en-route-btn-${job.id}`}
                      >
                        <Navigation className="w-4 h-4 mr-1.5" /> On Route
                      </Button>
                    )}
                    {job.current_status === "en_route" && (
                      <Button
                        onClick={() => markArrived(job.id)}
                        disabled={actionLoading === job.id}
                        className="flex-1 h-10 bg-zinc-900 hover:bg-zinc-800 text-white text-sm"
                        data-testid={`arrived-btn-${job.id}`}
                      >
                        <Home className="w-4 h-4 mr-1.5" /> Arrived
                      </Button>
                    )}
                    {job.current_status === "in_progress" && (
                      <Button
                        onClick={() => completeJob(job.id)}
                        disabled={actionLoading === job.id}
                        className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                        data-testid={`complete-job-${job.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete Job
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Late Reason Dialog */}
      <Dialog open={!!lateDialogJob} onOpenChange={() => setLateDialogJob(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-700">Late Arrival Report</DialogTitle>
          </DialogHeader>
          {lateDialogJob && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-xs text-red-600 font-medium">
                  {lateDialogJob.request_code} - {lateDialogJob.service_type_label}
                </p>
                <p className="text-[10px] text-red-500 mt-1">
                  Accepted at {new Date(lateDialogJob.accepted_at).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-700">
                  Please provide a reason for the delay:
                </label>
                <Textarea
                  value={lateReason}
                  onChange={(e) => setLateReason(e.target.value)}
                  rows={3}
                  placeholder="Traffic, prior job ran long, etc."
                  className="mt-1.5"
                  data-testid="late-reason-input"
                />
              </div>
              <Button
                onClick={submitLateReason}
                disabled={submittingReason || !lateReason.trim()}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="submit-late-reason-btn"
              >
                {submittingReason ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submit Reason
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Arrival Confirmation Dialog */}
      <Dialog open={!!showArrivalConfirm} onOpenChange={() => { setShowArrivalConfirm(null); setArrivalData(null); setHomeownerCode(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {arrivalData ? "Arrival Confirmed" : "Confirm Arrival"}
            </DialogTitle>
          </DialogHeader>
          {!arrivalData ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-center">
                <Home className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-blue-900">Are you at the homeowner's property?</p>
                <p className="text-xs text-blue-600 mt-1">This will notify the homeowner and start the job clock.</p>
              </div>
              <Button onClick={confirmArrival} disabled={actionLoading}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="confirm-arrival-button">
                {actionLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Yes, I Have Arrived</>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-900">Your arrival is confirmed!</p>
              </div>
              <div className="p-4 bg-zinc-900 rounded-xl text-center">
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-2">
                  Homeowner Confirmation Code
                </p>
                <p className="text-3xl font-mono font-bold text-white tracking-widest" data-testid="confirmation-code">
                  {arrivalData.confirmation_code}
                </p>
                <p className="text-xs text-zinc-400 mt-2">
                  Share this code with the homeowner to confirm your arrival
                </p>
              </div>
              {/* Share Link with Homeowner */}
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-xs text-blue-800 font-medium mb-2 flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5" /> Share confirmation link with homeowner
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="copy-homeowner-link"
                  onClick={async () => {
                    const { data } = await API.get(`/requests/${showArrivalConfirm}/homeowner-link`);
                    const link = `${window.location.origin}/confirm-arrival/${showArrivalConfirm}?t=${data.token}`;
                    await navigator.clipboard.writeText(link);
                    toast.success("Link copied! Share with homeowner.");
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Confirmation Link
                </Button>
              </div>

              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs text-zinc-500 mb-2 font-medium">Or enter the homeowner's confirmation here:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={homeownerCode}
                    onChange={e => setHomeownerCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono text-center tracking-wider"
                    maxLength={6}
                    data-testid="homeowner-code-input"
                  />
                  <Button
                    onClick={() => confirmHomeowner(showArrivalConfirm, homeownerCode || arrivalData.confirmation_code)}
                    disabled={confirmingHomeowner}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid="submit-homeowner-confirm"
                  >
                    {confirmingHomeowner ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
                  </Button>
                </div>
              </div>
              <Button variant="outline" onClick={() => { setShowArrivalConfirm(null); setArrivalData(null); }}
                className="w-full text-xs" data-testid="close-arrival-dialog">
                Done — I'll get homeowner confirmation later
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Acceptance PIN Dialog */}
      <Dialog open={!!acceptancePinDialog} onOpenChange={() => setAcceptancePinDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Job Completed - Customer PIN</DialogTitle>
          </DialogHeader>
          {acceptancePinDialog && (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-900">Job marked as completed!</p>
              </div>
              <div className="p-4 bg-zinc-900 rounded-xl text-center">
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-2">
                  Customer Acceptance PIN
                </p>
                <p className="text-4xl font-mono font-bold text-white tracking-[0.3em]" data-testid="acceptance-pin-display">
                  {acceptancePinDialog.pin}
                </p>
                <p className="text-xs text-zinc-400 mt-2">
                  Give this PIN to the customer to confirm job acceptance
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-xs text-blue-800 font-medium mb-2">Share acceptance link with customer:</p>
                <Button variant="outline" size="sm"
                  className="w-full text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="copy-acceptance-link"
                  onClick={async () => {
                    const { data } = await API.get(`/requests/${acceptancePinDialog.jobId}/homeowner-link`);
                    const link = `${window.location.origin}/accept-job/${acceptancePinDialog.jobId}?t=${data.token}`;
                    await navigator.clipboard.writeText(link);
                    toast.success("Acceptance link copied!");
                  }}>
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Acceptance Link
                </Button>
              </div>
              <Button variant="outline" onClick={() => setAcceptancePinDialog(null)}
                className="w-full text-xs" data-testid="close-pin-dialog">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
